import { useState, useEffect } from 'react';
import { Drawer, Box, Typography, Button, IconButton, CircularProgress } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { X } from 'lucide-react';
import { usePicklists } from '@lingaai/shared-hooks';
import { useCurrentUser } from '@lingaai/user-management';
import { createNode, updateNode, createEdge, getEdges, deleteEdge } from '@lingaai/supabase-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Node, CreateNodeInput } from '@lingaai/types';
import { NODE_REGISTRY as NODE_TYPE_CONFIG } from '@lingaai/types/graph/nodes';
import { useToast } from '../../contexts/ToastContext';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';
import { FormField } from '../forms/FormField';

interface NodeDrawerProps {
  open: boolean;
  onClose: () => void;
  nodeType: string;
  mode: 'create' | 'edit';
  node?: Node;
}

/**
 * Generic drawer for creating/editing any node type.
 * Form fields are driven by NODE_TYPE_CONFIG.
 */
export function NodeDrawer({ open, onClose, nodeType, mode, node }: NodeDrawerProps) {
  const { data: currentUser } = useCurrentUser();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<Record<string, unknown>>({});

  const createMutation = useMutation({
    mutationFn: (input: CreateNodeInput) => createNode(input),
    onSuccess: (newNode) => {
      queryClient.invalidateQueries({ queryKey: ['nodes', newNode.type] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, props }: { id: string; props: Record<string, unknown> }) => updateNode(id, { props }, nodeType),
    onSuccess: (updatedNode) => {
      queryClient.invalidateQueries({ queryKey: ['nodes', updatedNode.type] });
      queryClient.invalidateQueries({ queryKey: ['node', updatedNode.id] });
    },
  });

  const nodeConfig = NODE_TYPE_CONFIG[nodeType];

  // Fetch all picklists for the tenant
  const { data: allPicklists } = usePicklists();

  // Build a map of picklistName -> options
  const picklistOptionsMap: Record<string, Array<{ id: string; label: string }>> = {};
  if (allPicklists) {
    for (const picklist of allPicklists) {
      picklistOptionsMap[picklist.name] = picklist.items.map((item: { id: string; label: string }) => ({
        id: item.id,
        label: item.label,
      }));
    }
  }

  // Initialize form data when drawer opens or node changes
  useEffect(() => {
    if (open && nodeConfig) {
      if (mode === 'edit' && node) {
        // Load node props
        const initialData = { ...node.props };

        // Load lookup field values from edges
        const loadLookups = async () => {
          for (const field of nodeConfig.formFields) {
            if (field.type === 'lookup' && field.lookupConfig) {
              const edges = await getEdges({
                src: field.lookupConfig.direction === 'outgoing' ? node.id : undefined,
                dst: field.lookupConfig.direction === 'incoming' ? node.id : undefined,
                type: field.lookupConfig.edgeType,
              });

              if (edges && edges.length > 0) {
                const edge = edges[0];
                if (edge) {
                  const relatedNodeId = field.lookupConfig.direction === 'outgoing' ? edge.dst : edge.src;
                  initialData[field.key] = relatedNodeId;
                }
              }
            }
          }
          setFormData(initialData);
        };

        loadLookups();
      } else {
        // Initialize with empty values for create mode
        const initialData: Record<string, unknown> = {};
        nodeConfig.formFields.forEach((field) => {
          initialData[field.key] = '';
        });
        setFormData(initialData);
      }
    }
  }, [open, mode, node, nodeConfig]);

  if (!nodeConfig) {
    return null;
  }

  const handleFieldChange = (key: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!currentUser?.tenant_id) {
      showToast('No tenant context available', 'error');
      return;
    }
    try {
      // Separate lookup fields from regular props
      const lookupFields = nodeConfig.formFields.filter((f) => f.type === 'lookup');
      const regularProps: Record<string, unknown> = {};
      const lookupValues: Record<string, string> = {};

      for (const [key, value] of Object.entries(formData)) {
        const field = nodeConfig.formFields.find((f) => f.key === key);
        if (field?.type === 'lookup') {
          if (value && typeof value === 'string') {
            lookupValues[key] = value;
          }
        } else if (!key.includes('.')) {
          // Skip virtual fields (e.g., organization.name)
          // Skip empty strings - they fail optional field validation
          if (value !== '') {
            regularProps[key] = value;
          }
        }
      }

      let nodeId: string;

      if (mode === 'create') {
        const newNode = await createMutation.mutateAsync({
          tenant_id: currentUser.tenant_id,
          type: nodeType,
          props: regularProps,
        });
        nodeId = newNode.id;
        showToast(`${nodeConfig.displayName} created successfully`, 'success');
      } else if (node) {
        await updateMutation.mutateAsync({
          id: node.id,
          props: regularProps,
        });
        nodeId = node.id;
        showToast(`${nodeConfig.displayName} updated successfully`, 'success');
      } else {
        return;
      }

      // Handle lookup field edges
      for (const field of lookupFields) {
        if (!field.lookupConfig) continue;

        const lookupValue = lookupValues[field.key];

        // Get existing edges
        const existingEdges = await getEdges({
          src: field.lookupConfig.direction === 'outgoing' ? nodeId : undefined,
          dst: field.lookupConfig.direction === 'incoming' ? nodeId : undefined,
          type: field.lookupConfig.edgeType,
        });

        // Check if edge already exists with same target
        let edgeAlreadyExists = false;
        if (existingEdges && existingEdges.length > 0) {
          const oldEdge = existingEdges[0];
          if (oldEdge) {
            const oldRelatedId = field.lookupConfig.direction === 'outgoing' ? oldEdge.dst : oldEdge.src;

            if (oldRelatedId === lookupValue) {
              edgeAlreadyExists = true;
            } else {
              await deleteEdge(oldEdge.id);
            }
          }
        }

        // Create new edge if value is set and edge doesn't already exist
        if (lookupValue && !edgeAlreadyExists) {
          await createEdge({
            tenant_id: currentUser.tenant_id,
            type: field.lookupConfig.edgeType,
            src: field.lookupConfig.direction === 'outgoing' ? nodeId : lookupValue,
            dst: field.lookupConfig.direction === 'outgoing' ? lookupValue : nodeId,
            props: field.lookupConfig.edgeProps || {},
          });
        }
      }

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['nodes'] });
      queryClient.invalidateQueries({ queryKey: ['edges'] });

      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Operation failed';
      showToast(message, 'error');
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const title = mode === 'create' ? `Create ${nodeConfig.displayName}` : `Edit ${nodeConfig.displayName}`;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Drawer anchor="right" open={open} onClose={onClose}>
        <Box sx={{ width: 450, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">{title}</Typography>
            <IconButton onClick={onClose} size="small">
              <X size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {nodeConfig.formFields
              .filter((field) => !field.key.includes('.') || field.key.startsWith('geo.'))
              .map((field) => (
                <FormField
                  key={field.key}
                  field={field}
                  value={formData[field.key]}
                  onChange={(value) => handleFieldChange(field.key, value)}
                  picklistOptions={field.picklistName ? picklistOptionsMap[field.picklistName] : undefined}
                />
              ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
            <Button variant="outlined" onClick={onClose} fullWidth disabled={isPending}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSubmit} fullWidth disabled={isPending} startIcon={isPending ? <CircularProgress size={16} /> : undefined}>
              {isPending ? 'Saving...' : mode === 'create' ? 'Create' : 'Save'}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </LocalizationProvider>
  );
}
